// src/screens/admin/rawMaterialsList/RawMaterialsList.jsx
//
// Data: local useState dummy data (no context)
// To replace with API: see comment above DUMMY_MATERIALS
//
// KEPT SEPARATE: AddRawMaterial, EditRawMaterial,
//                DeleteRawMaterialConfirmation, RestockMaterial (subscreens)

import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  AlertTriangle,
  History,
  Bell,
} from "lucide-react";
import AddRawMaterial from "../../../subscreen/admin/addRawMaterial/AddRawMaterial";
import EditRawMaterial from "../../../subscreen/admin/editRawMaterial/EditRawMaterial";
import DeleteRawMaterialConfirmation from "../../../subscreen/admin/deleteRawMaterialConfirmation/DelteRawMaterialConfirmation";
import RestockMaterial from "../../../subscreen/admin/restockMaterial/RestockMaterial";
import "../../../assets/css/admin/rawMaterialList/RawMaterialList.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

const DUMMY_MATERIALS = [
//   {
//     id: "RM001",
//     name: "Oak Wood Planks",
//     category: "Wood",
//     currentStock: 15,
//     minimumStock: 20,
//     reorderPoint: 50,
//     unit: "pcs",
//     unitPrice: 1200,
//     supplier: "Punjab Timber Co.",
//   },
//   {
//     id: "RM002",
//     name: "Foam Padding",
//     category: "Upholstery",
//     currentStock: 30,
//     minimumStock: 10,
//     reorderPoint: 40,
//     unit: "kg",
//     unitPrice: 450,
//     supplier: "Lahore Foam Mills",
//   },
//   {
//     id: "RM003",
//     name: "Steel Screws (M6)",
//     category: "Hardware",
//     currentStock: 80,
//     minimumStock: 100,
//     reorderPoint: 200,
//     unit: "pcs",
//     unitPrice: 5,
//     supplier: "Gujranwala Hardware",
//   },
//   {
//     id: "RM004",
//     name: "Fabric Roll (Brown)",
//     category: "Upholstery",
//     currentStock: 5,
//     minimumStock: 8,
//     reorderPoint: 20,
//     unit: "meters",
//     unitPrice: 800,
//     supplier: "Faisalabad Textiles",
//   },
//   {
//     id: "RM005",
//     name: "Wood Varnish",
//     category: "Finishing",
//     currentStock: 50,
//     minimumStock: 5,
//     reorderPoint: 15,
//     unit: "liters",
//     unitPrice: 650,
//     supplier: "Karachi Paints Ltd.",
//   },
];

// ─────────────────────────────────────────────────────────────
// STOCK STATUS HELPER
// ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  critical: {
    label: "Critical",
    className: "rm-status-critical",
    rowClass: "rm-row-critical",
  },
  warning: {
    label: "Low Stock",
    className: "rm-status-warning",
    rowClass: "rm-row-warning",
  },
  good: { label: "In Stock", className: "rm-status-good", rowClass: "" },
};

const getStockStatus = ({ currentStock, minimumStock }) => {
  if (currentStock <= minimumStock) return "critical";
  //if (currentStock <= reorderPoint) return "warning";
  return "good";
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const RawMaterialsList = () => {
  const {baseUrl, authToken} = useContext(AppContext);
  const [rawMaterials, setRawMaterials] = useState([]);
  const navigate = useNavigate();

  // Store full material OBJECT (not just id) so subscreens get data via props
  const [editTarget, setEditTarget] = useState(null); // full material object
  const [restockTarget, setRestockTarget] = useState(null); // full material object
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [isDeleting, setIsDeleting] = useState(false);
  const [refresh, setRefresh] = useState(false);
  //const [AllMaterials, setAllMaterials] = useState();


  const deleteInventory = (idToDelete) => {
    console.log("*********");
    console.log("delete material called!");
    console.log(idToDelete);
    console.log("*********");
    const formData = new FormData();
    formData.append("id", idToDelete);
    axios.post(`${baseUrl}/admin/inventory/deleteInventory`, formData, {
      headers:
      {
        Authorization: `Bearer${authToken}`
      }
    }).then((response) => {
      console.log(response.data);
      viewAllInventory();
      setIsDeleting(false);
      setDeleteTarget(null);
    }).catch((error) => {
      console.log(error);
    })
  }

  const viewAllInventory = () => {
    console.log("view inventory called");
    axios.get(`${baseUrl}/admin/inventory/viewAllInventory`, {
      headers: {
        Authorization: `Bearer${authToken}`
      },
    })
    .then((response) => {
      setRawMaterials(response.data.rawMaterials);
    })
    .catch((error) => {
      console.log(error)
    })
  }

   useEffect(() => {
      viewAllInventory()
    }, [refresh]);

  const lowStockCount = rawMaterials.filter(
    (m) => getStockStatus(m) !== "good",
  ).length;

  // ── Open Edit / Restock with full object ──
  const handleEdit = (id) =>
    setEditTarget(rawMaterials.find((m) => m.id === id));
  const handleRestock = (id) =>
    setRestockTarget(rawMaterials.find((m) => m.id === id));

  // ── Called by EditRawMaterial on save ──
  const handleUpdateMaterial = (id, updatedData) => {
    setRawMaterials((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...updatedData } : m)),
    );
    setEditTarget(null);
  };

  // ── Called by RestockMaterial on save ──
  const handleAddStock = (id, quantity) => {
    setRawMaterials((prev) =>
      prev.map((m) =>
        m.id === id
          ? { ...m, currentStock: m.currentStock + Number(quantity) }
          : m,
      ),
    );
    setRestockTarget(null);
  };

  // ── Called by AddRawMaterial on save ──
  const handleAddMaterial = (newMaterial) => {
    setRawMaterials((prev) => [...prev, newMaterial]);
    setIsAddOpen(false);
  };

  // ── Delete ──
  const handleDelete = (id, name) => setDeleteTarget({ id, name });
  const handleCloseDelete = () => {
    if (!isDeleting) setDeleteTarget(null);
  };
  const handleConfirmDelete = () => {
    setIsDeleting(true);
    deleteInventory(deleteTarget.id);
    // setTimeout(() => {
    //   setRawMaterials((prev) => prev.filter((m) => m.id !== deleteTarget.id));
    //   setIsDeleting(false);
    //   setDeleteTarget(null);
    // }, 1000);
  };

  return (
    <div className="rm-container">
      {/* ── Low Stock Alert Banner ── */}
      {lowStockCount > 0 && (
        <div className="rm-alert-banner">
          <AlertTriangle size={18} />
          <span>
            <strong>{lowStockCount}</strong> material
            {lowStockCount > 1 ? "s" : ""} running low on stock
          </span>
          <button
            className="rm-alert-link"
            onClick={() => navigate("/admin/stock-alerts")}
          >
            View Alerts →
          </button>
        </div>
      )}

      {/* ── Header ── */}
      <div className="rm-header">
        <div>
          <h1 className="rm-title">Raw Materials</h1>
          <p className="rm-subtitle">Manage inventory and track stock levels</p>
        </div>
        <div className="rm-header-actions">
          <button
            className="rm-secondary-btn"
            onClick={() => navigate("/admin/stock-history")}
          >
            <History size={15} />
            History
          </button>
          <button
            className="rm-secondary-btn rm-alerts-btn"
            onClick={() => navigate("/admin/stock-alerts")}
          >
            <Bell size={15} />
            Alerts
            {lowStockCount > 0 && (
              <span className="rm-alert-badge">{lowStockCount}</span>
            )}
          </button>
          <button className="rm-add-btn" onClick={() => setIsAddOpen(true)}>
            <Plus size={16} />
            Add Material
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="rm-table-container">
        {rawMaterials.length === 0 ? (
          <div className="rm-empty">
            <span className="rm-empty-icon">📦</span>
            <h3>No materials found</h3>
            <p>Add your first raw material to get started</p>
          </div>
        ) : (
          <table className="rm-table">
            <thead>
              <tr>
                <th>Material Name</th>
                <th>Category</th>
                <th>Current Stock</th>
                <th>Min Stock</th>
                <th>Unit Price</th>
                <th>Supplier</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rawMaterials.map((m) => {
                const status = getStockStatus(m);
                const { label, className, rowClass } = STATUS_CONFIG[status];
                return (
                  <tr key={m.id} className={rowClass}>
                    <td>
                      <div className="rm-material-name">{m.name}</div>
                    </td>
                    <td>
                      <span className="rm-category">{m.adcategory?.name}</span>
                    </td>
                    <td>
                      <span className={`rm-stock-pill rm-stock-${status}`}>
                        {m.currentStock} {m.unit}
                      </span>
                    </td>
                    <td className="rm-min-stock">
                      {m.minimumStock} {m.unit}
                    </td>
                    <td className="rm-price">
                      PKR {m.unitPrice.toLocaleString()}
                    </td>
                    <td className="rm-supplier">{m.supplier}</td>
                    <td>
                      <span className={`rm-status-badge ${className}`}>
                        {label}
                      </span>
                    </td>
                    <td>
                      <div className="rm-actions">
                        <button
                          className="rm-action-btn rm-restock"
                          title="Restock"
                          onClick={() => handleRestock(m.id)}
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          className="rm-action-btn rm-edit"
                          title="Edit"
                          onClick={() => handleEdit(m.id)}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="rm-action-btn rm-delete"
                          title="Delete"
                          onClick={() => handleDelete(m.id, m.name)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modals ── */}
      {isAddOpen && (
        <AddRawMaterial
          isOpen
          onClose={() => setIsAddOpen(false)}
          triggerRefresh={() => setRefresh(prev => !prev)}
          onAdd={handleAddMaterial}
        />
      )}

      {/* material object directly — no context/id lookup needed */}
      {editTarget && (
        <EditRawMaterial
          isOpen
          onClose={() => setEditTarget(null)}
          material={editTarget}
          onUpdate={(updatedData) =>
            handleUpdateMaterial(editTarget.id, updatedData)
          }
        />
      )}

      {restockTarget && (
        <RestockMaterial
          isOpen
          onClose={() => setRestockTarget(null)}
          material={restockTarget}
          onAddStock={(qty) => handleAddStock(restockTarget.id, qty)}
        />
      )}

      {deleteTarget && (
        <DeleteRawMaterialConfirmation
          isOpen
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
          materialName={deleteTarget.name}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default RawMaterialsList;
